import { CSSProperties } from "react"
import { isNumber } from "lodash"
import { useTranslation } from "react-i18next"

import { Sort, TableHeaderColumn } from "@/app/report/common/constructor"
import { DataTable } from "@/app/report/common/type"
import { getLocaleString, resolveDecimalPoint } from "@/app/report/common/tools"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook"
import { countLeadingFixedColumns } from "@/app/report/hooks/useCustomTableData"

import "./printableTable.scss"

/**
 * 표 한 장(머리글 + 행)의 PDF 렌더러 — 리포트 PDF(PrintableTableSection)와 PDF 빌더(BuilderDataTable)가
 * 같은 마크업·스타일(printableTable.scss)로 표를 그리기 위한 단일 구현 (일감 #75).
 * 페이지/열 분할은 호출한 쪽이 맡고, 이 컴포넌트는 넘겨받은 머리글·행을 그대로 그린다.
 * 부모에 `renewal-pdf-container renewal-pdf table-container preview` 클래스가 있어야 스타일이 적용된다.
 */
export default function PrintableTableChunk({
  tableHeader,
  tableBody,
  sort,
  hasSummaryRow = false,
  currentDecimalPoint = 1,
  style,
  headerRowStyle,
  columnWidths,
  caption,
  onHeaderClick,
}: {
  tableHeader: TableHeaderColumn[]
  tableBody: DataTable
  sort?: Sort
  /** 상단에 Total/Average 요약 행이 있는지 — 행 음영(zebra) 반전과 요약 행 라벨 병합의 기준 */
  hasSummaryRow?: boolean
  currentDecimalPoint?: number
  style?: CSSProperties
  /** 머리글 행 높이 통일 등 호출부의 배치 요구 */
  headerRowStyle?: CSSProperties
  /** 컬럼 키 → 폭(px). 호출부가 분할 폭을 계산해 넘길 때 */
  columnWidths?: Record<string, number>
  caption?: { title: string; countText?: string }
  /** 머리글 클릭 정렬 — PDF 빌더 Table 이 넘긴다 (#185). 없으면 클릭 동작 없음 */
  onHeaderClick?: (key: string) => void
}) {
  const { t } = useTranslation()
  const translateKey = useTranslateMetricKey()

  const getColumnUnit = (indexCol: number): string => {
    switch (tableHeader[indexCol]?.key) {
      case "duration":
        return "min sec"
      default: {
        let unit = tableHeader[indexCol]?.unit
        if (unit?.includes("^2")) {
          unit = unit.replace("^2", "²")
        }

        return unit ?? ""
      }
    }
  }

  const getValue = (value: string | number | null, decimalPoint: number) => {
    if (value === null) {
      return ""
    } else if (isNumber(value)) {
      return getLocaleString(value, decimalPoint)
    }

    return value
  }

  const getCellValue = (indexRow: number, indexCol: number) => {
    const cell = tableBody?.[indexRow]?.[indexCol] ?? "-"
    // 정수 단위(bpm) 컬럼은 소수 자릿수 선택과 무관하게 정수 (#169)
    const decimalPoint = resolveDecimalPoint(tableHeader[indexCol]?.unit, currentDecimalPoint ?? 1)

    switch (tableHeader[indexCol]?.key) {
      case "activity":
      case "date": {
        return cell
      }
      case "activityType":
        return t(cell as unknown as string).toString()
      default:
        return getValue(cell as unknown as string | number | null, decimalPoint)
    }
  }

  return (
    <table style={style}>
      {caption && (
        <caption>
          <div className="section-name">
            <span className="section-title-main">{caption.title}</span>
            <span className="item-count-text">{caption.countText ?? ""}</span>
          </div>
        </caption>
      )}
      <thead>
        <tr className="table-header-background" style={headerRowStyle}>
          {tableHeader?.map((columnHeader: TableHeaderColumn, indexCol: number) => (
            <th
              key={`table_header_${indexCol}`}
              className={`table-header-background ${columnHeader.key}`}
              style={
                columnWidths?.[columnHeader.key]
                  ? { width: columnWidths[columnHeader.key] }
                  : undefined
              }>
              <div className="table-header-column" data-column_index={indexCol}>
                <div
                  className="table-header-title"
                  role="button"
                  style={onHeaderClick ? { cursor: "pointer" } : undefined}
                  onClick={onHeaderClick ? () => onHeaderClick(columnHeader.key) : undefined}>
                  <div className="table-cell-div">{translateKey(columnHeader.title)}</div>
                  <div className="icon-sort">
                    {sort?.key === columnHeader.key && sort?.order === "ASC" && <span>▲</span>}
                    {sort?.key === columnHeader.key && sort?.order === "DESC" && <span>▼</span>}
                  </div>
                </div>
                <div className="table-header-subtitle">{getColumnUnit(indexCol)}&nbsp;</div>
              </div>
            </th>
          ))}
        </tr>
      </thead>
      {tableBody?.length === 0 ? (
        <tbody>
          <tr>
            <td colSpan={tableHeader?.length}>
              <div className="empty">{t("messages.noDataAvailable")}</div>
            </td>
          </tr>
        </tbody>
      ) : (
        <tbody>
          {tableBody?.map((row, indexRow: number) => {
            const isSummaryRow = row.includes(t("total")) || row.includes(t("average"))
            // 요약 행 라벨은 첫 열부터 연속된 고정 컬럼을 병합 — 화면(CustomTableSection colSpan)과 동일 규칙 (#98)
            const labelColSpan =
              hasSummaryRow && isSummaryRow ? Math.max(1, countLeadingFixedColumns(tableHeader)) : 1

            return (
              <tr
                key={`table_body_row_${indexRow}`}
                className={`${hasSummaryRow ? "isDaily" : ""} ${isSummaryRow ? "table-summary" : ""}`}>
                {row.map((_, indexCol: number) => {
                  if (indexCol > 0 && indexCol < labelColSpan) {
                    return null
                  }

                  return (
                    <td
                      key={`table_body_col_${indexCol}`}
                      colSpan={indexCol === 0 && labelColSpan > 1 ? labelColSpan : undefined}>
                      <div className={`table-cell-div ${tableHeader[indexCol]?.key}`}>
                        {getCellValue(indexRow, indexCol)}
                      </div>
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      )}
    </table>
  )
}
